import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Eye,
  RefreshCw,
  FileWarning,
  DollarSign,
  Clock,
  ShieldAlert,
  ShieldCheck,
  Image,
  Calendar,
  User,
  ExternalLink,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const API = `${BACKEND_URL}/api`;

const FraudAlerts = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    confirmed_fraud: 0,
    dismissed: 0
  });
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchAlerts = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const statusFilter = activeTab === 'all' ? '' : activeTab;
      
      const response = await axios.get(`${API}/fraud-alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: statusFilter || undefined }
      });
      
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Failed to fetch fraud alerts:', error);
      toast.error('Failed to load fraud alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleRefresh = () => {
    fetchAlerts(false);
    toast.success('Fraud alerts refreshed!');
  };

  const openReviewDialog = (alert) => {
    setSelectedAlert(alert);
    setReviewStatus('');
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReview = async () => {
    if (!reviewStatus) {
      toast.error('Please select a status');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/fraud-alerts/${selectedAlert.id}`, {
        status: reviewStatus,
        review_notes: reviewNotes || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Alert reviewed successfully');
      setReviewDialogOpen(false);
      fetchAlerts(false);
    } catch (error) {
      console.error('Failed to review alert:', error);
      toast.error('Failed to review alert');
    } finally {
      setSubmitting(false);
    }
  };

  const openImageDialog = (url, title) => {
    setSelectedImage({ url, title });
    setImageDialogOpen(true);
  };


  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      reviewed: 'bg-blue-100 text-blue-800 border-blue-300',
      confirmed_fraud: 'bg-red-100 text-red-800 border-red-300',
      dismissed: 'bg-gray-100 text-gray-600 border-gray-300'
    };
    const labels = {
      pending: 'Pending Review',
      reviewed: 'Reviewed',
      confirmed_fraud: 'Confirmed Fraud',
      dismissed: 'Dismissed'
    };
    return (
      <Badge className={`${styles[status] || styles.pending} border`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getSimilarityColor = (score) => {
    if (score >= 90) return 'text-red-600 bg-red-50';
    if (score >= 70) return 'text-orange-600 bg-orange-50';
    if (score >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-500" />
            Fraud Alerts
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            AI-powered duplicate bill detection
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-lg">
                <FileWarning className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Alerts</p>
                <p className="text-xl font-bold text-gray-800">{summary.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-200 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-700" />
              </div>
              <div>
                <p className="text-xs text-yellow-600">Pending Review</p>
                <p className="text-xl font-bold text-yellow-800">{summary.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-700" />
              </div>
              <div>
                <p className="text-xs text-red-600">Confirmed Fraud</p>
                <p className="text-xl font-bold text-red-800">{summary.confirmed_fraud}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-200 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <p className="text-xs text-green-600">Dismissed</p>
                <p className="text-xl font-bold text-green-800">{summary.dismissed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-xs text-blue-600">Reviewed</p>
                <p className="text-xl font-bold text-blue-800">{summary.reviewed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="w-4 h-4" />
            Pending ({summary.pending})
          </TabsTrigger>
          <TabsTrigger value="confirmed_fraud" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Confirmed ({summary.confirmed_fraud})
          </TabsTrigger>
          <TabsTrigger value="dismissed" className="gap-2">
            <XCircle className="w-4 h-4" />
            Dismissed ({summary.dismissed})
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            All ({summary.total})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Loading alerts...</p>
            </div>
          ) : alerts.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <ShieldCheck className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-600 font-medium">No alerts found</p>
                <p className="text-gray-400 text-sm mt-1">
                  {activeTab === 'pending' 
                    ? 'All clear! No pending fraud alerts.' 
                    : `No ${activeTab.replace('_', ' ')} alerts.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <Card key={alert.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      {/* Similarity Score */}
                      <div className={`flex-shrink-0 p-4 rounded-lg ${getSimilarityColor(alert.similarity_score)} text-center min-w-[100px]`}>
                        <p className="text-3xl font-bold">{Math.round(alert.similarity_score)}%</p>
                        <p className="text-xs uppercase tracking-wide">Match</p>
                      </div>

                      {/* Alert Details */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              Potential Duplicate Bill Detected
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              Created {new Date(alert.created_at).toLocaleString('en-IN')}
                            </p>
                          </div>
                          {getStatusBadge(alert.status)}
                        </div>

                        {/* Bill Comparison */}
                        <div className="grid md:grid-cols-2 gap-4 mt-3">
                          {/* New Bill */}
                          <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                            <p className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
                              <FileWarning className="w-3 h-3" />
                              NEW BILL (Flagged)
                            </p>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-gray-500">Vendor:</span> <span className="font-medium">{alert.bill_vendor}</span></p>
                              <p><span className="text-gray-500">Bill #:</span> {alert.bill_number}</p>
                              <p><span className="text-gray-500">Amount:</span> <span className="font-semibold">{formatCurrency(alert.bill_amount)}</span></p>
                              <p><span className="text-gray-500">Date:</span> {alert.bill_date}</p>
                            </div>
                            {alert.bill_photo_url && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="mt-2 w-full text-xs"
                                onClick={() => openImageDialog(alert.bill_photo_url, 'New Bill Photo')}
                              >
                                <Image className="w-3 h-3 mr-1" />
                                View Image
                              </Button>
                            )}
                          </div>

                          {/* Arrow */}
                          <div className="hidden md:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
                            <ArrowRight className="w-6 h-6 text-gray-300" />
                          </div>

                          {/* Duplicate Bill */}
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              EXISTING BILL (Match)
                            </p>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-gray-500">Vendor:</span> <span className="font-medium">{alert.duplicate_bill_vendor}</span></p>
                              <p><span className="text-gray-500">Bill #:</span> {alert.duplicate_bill_number}</p>
                              <p><span className="text-gray-500">Amount:</span> <span className="font-semibold">{formatCurrency(alert.duplicate_bill_amount)}</span></p>
                              <p><span className="text-gray-500">Date:</span> {alert.duplicate_bill_date}</p>
                            </div>
                            {alert.duplicate_bill_photo_url && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="mt-2 w-full text-xs"
                                onClick={() => openImageDialog(alert.duplicate_bill_photo_url, 'Existing Bill Photo')}
                              >
                                <Image className="w-3 h-3 mr-1" />
                                View Image
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* AI Reason */}
                        {alert.ai_reason && (
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mt-3">
                            <p className="text-xs font-medium text-blue-600 mb-1">AI Analysis</p>
                            <p className="text-sm text-blue-800">{alert.ai_reason}</p>
                          </div>
                        )}

                        {/* Review Info */}
                        {alert.reviewed_by && (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-3">
                            <p className="text-xs font-medium text-gray-600 mb-1">Review Decision</p>
                            <p className="text-sm">
                              <span className="text-gray-500">Reviewed by:</span>{' '}
                              <span className="font-medium">{alert.reviewed_by_name}</span>
                              {' on '}{new Date(alert.reviewed_at).toLocaleString('en-IN')}
                            </p>
                            {alert.review_notes && (
                              <p className="text-sm mt-1 text-gray-700">{alert.review_notes}</p>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        {alert.status === 'pending' && (
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              onClick={() => openReviewDialog(alert)}
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Review Alert
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Review Fraud Alert
            </DialogTitle>
            <DialogDescription>
              Make a decision on this potential duplicate bill alert
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedAlert && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><span className="text-gray-500">Similarity:</span> <span className="font-bold">{Math.round(selectedAlert.similarity_score)}%</span></p>
                <p><span className="text-gray-500">Vendor:</span> {selectedAlert.bill_vendor}</p>
                <p><span className="text-gray-500">Amount:</span> {formatCurrency(selectedAlert.bill_amount)}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Decision</label>
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select decision..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed_fraud">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Confirm as Fraud
                    </span>
                  </SelectItem>
                  <SelectItem value="dismissed">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-gray-500" />
                      Dismiss (Not Fraud)
                    </span>
                  </SelectItem>
                  <SelectItem value="reviewed">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-500" />
                      Mark as Reviewed
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about your decision..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReview} disabled={submitting || !reviewStatus}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedImage?.title || 'Bill Image'}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            {selectedImage?.url && (
              <img 
                src={selectedImage.url} 
                alt={selectedImage.title}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => window.open(selectedImage?.url, '_blank')}
              className="gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              Open Full Size
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FraudAlerts;
